import 'bootstrap/dist/css/bootstrap.css';
import { useState, useMemo, useEffect } from 'react';
import Spinner from '../../shared/spinner';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
    faSearch, 
    faFileUpload, 
    faList, 
    faCheck, 
    faTimes, 
    faEye, 
    faFileInvoiceDollar, 
    faSync,
    faExclamationTriangle 
} from '@fortawesome/free-solid-svg-icons';
import CustomAlert from '../../shared/customAlert';
import { format } from 'date-fns';

import FaturaService from '../../services/faturaService';
import ComissaoService from '../../services/comissaoService';
import PromotoraService from '../../services/promotoraService';
import RegionalService from '../../services/regionalService';
import ComercialService from '../../services/comercialService';

interface ItemFaturaDTO {
  id: number;
  valorComissao: number;
  contrato: {
      numeroContrato: string;
      idCliente: number;
  };
  parametroUtilizado: {
      comissaoPerfil: { nome: string };
  };
}

interface FaturaDTO {
  id: number;
  nomeParceiro?: string;
  dataCriacao: string;
  itens: ItemFaturaDTO[]; 
  valorLiquido: number;
  status: string;
  idNotaFiscal?: number; 
  numeroNotaFiscal?: string;
  statusNotaFiscal?: string; 
  statusAutomacao?: string;
  statusOcr?: string;
  respostaOcr?: string;
}

interface OrigemDTO { id: number; nome: string; }

function GestaoNotaFiscalComponent() {
  
  const [alert, setAlert] = useState<{ message: string, type: 'success' | 'warning' | 'error' } | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadingOrigens, setLoadingOrigens] = useState(false);
  
  const [filtros, setFiltros] = useState({
      dataInicio: '',
      dataFim: '',
      status: '',
      numeroFatura: '',
      numeroNota: '',
      tipoOrigem: '',
      idOrigem: ''
  });

  const [listaFaturas, setListaFaturas] = useState<FaturaDTO[]>([]);
  const [listaOrigens, setListaOrigens] = useState<OrigemDTO[]>([]);
  
  const [idsSelecionados, setIdsSelecionados] = useState<number[]>([]);
  const [showModalUpload, setShowModalUpload] = useState(false);
  const [showModalDetalhes, setShowModalDetalhes] = useState(false);
  const [showModalAvaliacao, setShowModalAvaliacao] = useState(false);

  const [faturaParaDetalhes, setFaturaParaDetalhes] = useState<FaturaDTO | null>(null);
  const [faturaEmAvaliacao, setFaturaEmAvaliacao] = useState<FaturaDTO | null>(null);

  const [numeroNota, setNumeroNota] = useState('');
  const [valorNota, setValorNota] = useState('');
  const [arquivoNota, setArquivoNota] = useState<File | null>(null);
  const [motivoReprovacao, setMotivoReprovacao] = useState('');

  const faturaService = useMemo(() => new FaturaService(), []);
  const comissaoService = useMemo(() => new ComissaoService(), []);
  const promotoraService = useMemo(() => new PromotoraService(), []);
  const regionalService = useMemo(() => new RegionalService(), []);
  const comercialService = useMemo(() => new ComercialService(), []);

  useEffect(() => {
      buscarFaturas(); 
  }, []);

  const buscarOrigensPorTipo = async (tipo: string) => {
      setLoadingOrigens(true);
      setListaOrigens([]); 
      
      setFiltros(prev => ({ ...prev, tipoOrigem: tipo, idOrigem: '' }));

      if (!tipo) {
          setLoadingOrigens(false);
          return;
      }

      try {
          let response;
          let dadosFormatados: OrigemDTO[] = [];

          if (tipo === 'PROMOTORA') response = await promotoraService.listarTodasPromotorasAtivas();
          else if (tipo === 'REGIONAL') response = await regionalService.listarTodasRegionaisAtivas();
          else if (tipo === 'COMERCIAL') response = await comercialService.listarTodasComerciaisAtivas();

          const dadosRaw = response?.data || response || [];
          if (Array.isArray(dadosRaw)) {
              dadosFormatados = dadosRaw.map((item: any) => ({
                  id: item.id,
                  nome: item.nome || item.descricao || item.razaoSocial || `Item ${item.id}`
              }));
          }
          setListaOrigens(dadosFormatados);
      } catch (error) {
          setAlert({ message: 'Erro ao carregar lista de origens.', type: 'error' });
      } finally {
          setLoadingOrigens(false);
      }
  };

  const buscarFaturas = async () => {
    try {
      setLoading(true);
      setListaFaturas([]);
      setIdsSelecionados([]);
      
      const response = await faturaService.listarFaturas(filtros);
      const dados = Array.isArray(response.data) ? response.data : (response.data.content || []);
      
      setListaFaturas(dados);
      setLoading(false);
    } catch (err: any) {
      setLoading(false);
      console.error(err);
      setAlert({ message: 'Erro ao buscar faturas.', type: 'error' });
    }
  };

  const isElegivelParaUpload = (fatura: FaturaDTO) => {
      const statusNota = fatura.statusNotaFiscal?.toUpperCase();
      
      if (!statusNota) return true; 
      if (statusNota === 'REPROVADA' || statusNota === 'REPROVADO') return true; 
      return false; 
  };

  const toggleSelecionarTodos = (checked: boolean) => {
      if (checked) {
          const ids = listaFaturas.filter(f => isElegivelParaUpload(f)).map(f => f.id);
          setIdsSelecionados(ids);
      } else {
          setIdsSelecionados([]);
      }
  };

  const toggleSelecionarFatura = (id: number) => {
      if (idsSelecionados.includes(id)) setIdsSelecionados(idsSelecionados.filter(item => item !== id));
      else setIdsSelecionados([...idsSelecionados, id]);
  };

  const valorTotalSelecionado = useMemo(() => {
      return listaFaturas
        .filter(f => idsSelecionados.includes(f.id))
        .reduce((acc, curr) => acc + (curr.valorLiquido || 0), 0);
  }, [idsSelecionados, listaFaturas]);

  const abrirModalUpload = (idFaturaUnica?: number) => {
    if (idFaturaUnica) {
        setIdsSelecionados([idFaturaUnica]);
        const fatura = listaFaturas.find(f => f.id === idFaturaUnica);
        setValorNota(fatura ? (fatura.valorLiquido || 0).toFixed(2) : '0.00');
    } else {
        if (idsSelecionados.length === 0) return setAlert({ message: 'Selecione ao menos uma fatura elegível.', type: 'warning' });
        setValorNota(valorTotalSelecionado.toFixed(2));
    }
    setNumeroNota('');
    setArquivoNota(null);
    setShowModalUpload(true);
  };

  const enviarNotaFiscalEmLote = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!arquivoNota || idsSelecionados.length === 0) return;
    try {
      setLoading(true);
      const promises = idsSelecionados.map(idFatura => {
          const valorNumerico = parseFloat(valorNota.toString().replace('R$', '').replace(',', '.'));
          return comissaoService.uploadNotaFiscal(idFatura, numeroNota, valorNumerico, arquivoNota);
      });
      await Promise.all(promises);
      setAlert({ message: 'Notas enviadas com sucesso!', type: 'success' });
      setShowModalUpload(false);
      setIdsSelecionados([]);
      buscarFaturas();
    } catch (err: any) {
      setLoading(false);
      setAlert({ message: 'Erro ao enviar nota.', type: 'error' });
    } finally { setLoading(false); }
  };

  const abrirDetalhes = (fatura: FaturaDTO) => {
      setFaturaParaDetalhes(fatura);
      setShowModalDetalhes(true);
  };

  const abrirModalAvaliacao = (fatura: FaturaDTO) => {
    if (!fatura.idNotaFiscal) return setAlert({ message: 'Sem ID de Nota Fiscal vinculado.', type: 'warning' });
    setFaturaEmAvaliacao(fatura);
    setMotivoReprovacao('');
    setShowModalAvaliacao(true);
  };

  const visualizarAnexo = async (idNota: number) => {
      try {
          setLoading(true);
          const response = await comissaoService.axios.get(`/notas/${idNota}/visualizar`, {
              responseType: 'blob'
          });
          
          const url = window.URL.createObjectURL(new Blob([response.data], { type: response.headers['content-type'] }));
          window.open(url, '_blank');
      } catch (e) {
          setAlert({ message: 'Erro ao visualizar anexo.', type: 'error' });
      } finally {
          setLoading(false);
      }
  };

  const processarAvaliacao = async (aprovar: boolean) => {
    if (!faturaEmAvaliacao || !faturaEmAvaliacao.idNotaFiscal) return;
    try {
      setLoading(true);
      if (aprovar) {
          await comissaoService.aprovarNota(faturaEmAvaliacao.idNotaFiscal);
          setAlert({ message: 'Nota Aprovada!', type: 'success' });
      } else {
          if (!motivoReprovacao) {
              setLoading(false);
              return setAlert({ message: 'Motivo é obrigatório.', type: 'warning' });
          }
          await comissaoService.reprovarNota(faturaEmAvaliacao.idNotaFiscal, motivoReprovacao);
          setAlert({ message: 'Nota Reprovada.', type: 'success' });
      }
      setShowModalAvaliacao(false);
      buscarFaturas();
    } catch (err) {
      setLoading(false);
      setAlert({ message: 'Erro ao processar avaliação.', type: 'error' });
    }
  };

  const formatMoney = (val: number) => val.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

  const getBadgeFatura = (status: string) => {
      const s = status ? status.toUpperCase() : '';
      switch(s) {
          case 'PAGA': case 'PAGO': return <span className="badge bg-success">Paga</span>;
          case 'APROVADA': return <span className="badge bg-primary">Aprovada</span>;
          case 'CANCELADA': return <span className="badge bg-dark">Cancelada</span>;
          default: return <span className="badge bg-secondary">{s}</span>;
      }
  };

  const getBadgeNota = (statusNota?: string) => {
      if (!statusNota) return <span className="badge bg-light text-muted border">Pendente</span>;
      const s = statusNota.toUpperCase();
      if (s === 'APROVADO' || s === 'APROVADA') return <span className="badge bg-success">Aprovada</span>;
      if (s === 'REPROVADO' || s === 'REPROVADA') return <span className="badge bg-danger">Reprovada</span>;
      if (s === 'PENDENTE') return <span className="badge bg-warning text-dark">Em Análise</span>;
      return <span className="badge bg-info text-dark">{s}</span>;
  };

  return (
    <>
      {alert && (<CustomAlert message={alert.message} type={alert.type} onClose={() => setAlert(null)} />)}
      
      <div className="position-relative m-md-3">
        {loading ? <Spinner loading={loading} /> : (
          <div className="card shadow-sm" style={{ marginTop: '25px' }}>
            <div className="card-body">
              <div className="card-header bg-white border-bottom-0 ps-0">
                <h5 className="mb-0">Financeiro &gt; Gestão de Notas Fiscais</h5>
              </div>

              <div className="p-3 bg-light rounded border mt-3 mb-4">
                <div className="row g-2 mb-2">
                    <div className="col-md-2">
                        <label className="form-label small fw-bold">Data Inicial</label>
                        <input type="date" className="form-control form-control-sm" 
                            value={filtros.dataInicio} 
                            onChange={e => setFiltros({...filtros, dataInicio: e.target.value})} 
                        />
                    </div>
                    <div className="col-md-2">
                        <label className="form-label small fw-bold">Data Final</label>
                        <input type="date" className="form-control form-control-sm" 
                            value={filtros.dataFim} 
                            onChange={e => setFiltros({...filtros, dataFim: e.target.value})} 
                        />
                    </div>
                    <div className="col-md-2">
                        <label className="form-label small fw-bold">Status Fatura</label>
                        <select className="form-select form-select-sm" 
                            value={filtros.status} 
                            onChange={e => setFiltros({...filtros, status: e.target.value})}
                        >
                            <option value="">Todos</option>
                            <option value="PAGA">Paga</option>
                            <option value="APROVADA">Aprovada</option>
                            <option value="EM_ANALISE">Em Análise</option>
                            <option value="CANCELADA">Cancelada</option>
                        </select>
                    </div>
                    <div className="col-md-2">
                        <label className="form-label small fw-bold">Nº Fatura</label>
                        <input type="text" className="form-control form-control-sm" placeholder="#"
                            value={filtros.numeroFatura} 
                            onChange={e => setFiltros({...filtros, numeroFatura: e.target.value})} 
                        />
                    </div>
                    <div className="col-md-2">
                        <label className="form-label small fw-bold">Nº Nota</label>
                        <input type="text" className="form-control form-control-sm" placeholder="#"
                            value={filtros.numeroNota} 
                            onChange={e => setFiltros({...filtros, numeroNota: e.target.value})} 
                        />
                    </div>
                </div>

                <div className="row g-2 align-items-end">
                    <div className="col-md-3">
                        <label className="form-label small fw-bold">Tipo Origem</label>
                        <select className="form-select form-select-sm" 
                            value={filtros.tipoOrigem} 
                            onChange={e => buscarOrigensPorTipo(e.target.value)}
                        >
                            <option value="">-- Selecione --</option>
                            <option value="PROMOTORA">Promotora</option>
                            <option value="REGIONAL">Regional</option>
                            <option value="COMERCIAL">Comercial</option>
                        </select>
                    </div>
                    
                    <div className="col-md-4">
                        <label className="form-label small fw-bold">
                            Origem / Parceiro 
                            {loadingOrigens && <FontAwesomeIcon icon={faSync} spin className="ms-2 text-muted"/>}
                        </label>
                        <select className="form-select form-select-sm" 
                            value={filtros.idOrigem} 
                            onChange={e => setFiltros({...filtros, idOrigem: e.target.value})}
                            disabled={!filtros.tipoOrigem || loadingOrigens}
                        >
                            <option value="">{loadingOrigens ? 'Carregando...' : 'Todos'}</option>
                            {listaOrigens.map(o => (
                                <option key={o.id} value={o.id}>{o.nome}</option>
                            ))}
                        </select>
                    </div>

                    <div className="col-md-2">
                        <button className="btn btn-primary btn-sm w-100" onClick={buscarFaturas}>
                            <FontAwesomeIcon icon={faSearch} className="me-2"/> Filtrar
                        </button>
                    </div>

                    <div className="col-md-3 text-end">
                        {idsSelecionados.length > 0 && (
                            <button className="btn btn-dark btn-sm w-100" onClick={() => abrirModalUpload()}>
                                <FontAwesomeIcon icon={faFileUpload} className="me-2" />
                                Enviar Lote ({idsSelecionados.length})
                            </button>
                        )}
                    </div>
                </div>
              </div>

              {listaFaturas.length > 0 ? (
                <div className="table-responsive border rounded">
                  <table className="table table-hover table-striped mb-0 align-middle">
                    <thead className="table-dark small">
                      <tr>
                        <th className="text-center" style={{ width: '40px' }}>
                            <input type="checkbox" onChange={e => toggleSelecionarTodos(e.target.checked)}
                                checked={listaFaturas.some(f => isElegivelParaUpload(f)) && idsSelecionados.length === listaFaturas.filter(f => isElegivelParaUpload(f)).length}
                            />
                        </th>
                        <th className="text-center" style={{ width: '5%' }}>ID</th>
                        <th>Parceiro / Origem</th>
                        <th className="text-center">Data</th>
                        <th className="text-end">Valor Líquido</th>
                        <th className="text-center">Status Fatura</th>
                        <th className="text-center">Status Nota</th>
                        <th className="text-center">Ações</th>
                      </tr>
                    </thead>
                    <tbody>
                      {listaFaturas.map((fatura) => {
                        const podeSelecionar = isElegivelParaUpload(fatura);
                        return (
                            <tr key={fatura.id}>
                              <td className="text-center">
                                  {podeSelecionar && (
                                      <input type="checkbox" checked={idsSelecionados.includes(fatura.id)} onChange={() => toggleSelecionarFatura(fatura.id)} />
                                  )}
                              </td>
                              <td className="text-center fw-bold text-muted">#{fatura.id}</td>
                              <td>{fatura.nomeParceiro || '---'}</td>
                              <td className="text-center">
                                  {fatura.dataCriacao ? format(new Date(fatura.dataCriacao), 'dd/MM/yyyy') : '-'}
                              </td>
                              <td className="text-end fw-bold text-success">{formatMoney(fatura.valorLiquido || 0)}</td>
                              
                              <td className="text-center">{getBadgeFatura(fatura.status)}</td>
                              
                              <td className="text-center">
                                  {getBadgeNota(fatura.statusNotaFiscal)}
                                  {fatura.numeroNotaFiscal && <div className="small text-muted mt-1"><FontAwesomeIcon icon={faFileInvoiceDollar}/> {fatura.numeroNotaFiscal}</div>}
                              </td>

                              <td className="text-center">
                                  <div className="btn-group btn-group-sm">
                                      <button className="btn btn-outline-info" title="Ver Itens" onClick={() => abrirDetalhes(fatura)}>
                                          <FontAwesomeIcon icon={faList} />
                                      </button>
                                      
                                      {podeSelecionar && (
                                          <button className="btn btn-outline-dark" title="Anexar Nota" onClick={() => abrirModalUpload(fatura.id)}>
                                              <FontAwesomeIcon icon={faFileUpload} />
                                          </button>
                                      )}

                                      {fatura.idNotaFiscal && (fatura.statusNotaFiscal === 'PENDENTE' || fatura.statusNotaFiscal === 'EM_ANALISE') && (
                                          <button className="btn btn-outline-primary" title="Avaliar Nota" onClick={() => abrirModalAvaliacao(fatura)}>
                                              <FontAwesomeIcon icon={faCheck} />
                                          </button>
                                      )}
                                  </div>
                              </td>
                            </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ) : (
                !loading && <div className="alert alert-light border text-center text-muted mt-3 py-4">Nenhuma fatura encontrada com os filtros selecionados.</div>
              )}
            </div>
          </div>
        )}

        {showModalUpload && (
            <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
                <div className="modal-dialog modal-dialog-centered">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h5 className="modal-title">Anexar Nota Fiscal</h5>
                            <button type="button" className="btn-close" onClick={() => setShowModalUpload(false)}></button>
                        </div>
                        <form onSubmit={enviarNotaFiscalEmLote}>
                            <div className="modal-body">
                                <div className="alert alert-primary">
                                    <small>Anexando para <strong>{idsSelecionados.length}</strong> fatura(s).</small><br/>
                                    <strong>Total: {formatMoney(valorTotalSelecionado)}</strong>
                                </div>
                                <div className="form-group mb-3">
                                    <label>Número da Nota <span className="text-danger">*</span></label>
                                    <input type="text" className="form-control" required value={numeroNota} onChange={e => setNumeroNota(e.target.value)} />
                                </div>
                                <div className="form-group mb-3">
                                    <label>Valor da Nota <span className="text-danger">*</span></label>
                                    <input type="text" className="form-control" required value={valorNota} onChange={e => setValorNota(e.target.value)} />
                                </div>
                                <div className="form-group mb-3">
                                    <label>Arquivo (PDF/Imagem) <span className="text-danger">*</span></label>
                                    <input type="file" className="form-control" accept="application/pdf, image/*" required onChange={e => setArquivoNota(e.target.files ? e.target.files[0] : null)} />
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-secondary" onClick={() => setShowModalUpload(false)}>Cancelar</button>
                                <button type="submit" className="btn btn-success">Enviar</button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        )}

        {showModalAvaliacao && faturaEmAvaliacao && (
            <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
                <div className="modal-dialog modal-dialog-centered">
                    <div className="modal-content">
                        <div className="modal-header bg-warning text-dark">
                            <h5 className="modal-title">Avaliação da Nota</h5>
                            <button type="button" className="btn-close" onClick={() => setShowModalAvaliacao(false)}></button>
                        </div>
                        <div className="modal-body">
                            
                            {(() => {
                                const stAuto = faturaEmAvaliacao.statusAutomacao;
                                const stOcr = faturaEmAvaliacao.statusOcr;

                                if (stAuto === 'CONCLUIDO' && stOcr === 'CONCLUIDO') {
                                    return (
                                        <div className="alert alert-success d-flex align-items-center" role="alert">
                                            <FontAwesomeIcon icon={faCheck} className="me-2 fs-4" />
                                            <div>
                                                <strong>Sucesso:</strong> Nota validada com sucesso na base central e no OCR.
                                            </div>
                                        </div>
                                    );
                                }

                                if (stOcr === 'ERRO') {
                                    return (
                                        <div className="alert alert-danger" role="alert">
                                            <div className="d-flex align-items-center mb-1">
                                                <FontAwesomeIcon icon={faExclamationTriangle} className="me-2 fs-4" />
                                                <strong>Falha na Validação OCR:</strong>
                                            </div>
                                            <div className="small" style={{ whiteSpace: 'pre-wrap' }}>{faturaEmAvaliacao.respostaOcr || 'Inconsistências encontradas na leitura da nota.'}</div>
                                        </div>
                                    );
                                }

                                if (!stAuto || stAuto === 'PENDENTE' || stAuto === 'ERRO') {
                                    return (
                                        <div className="alert alert-danger d-flex align-items-center" role="alert">
                                            <FontAwesomeIcon icon={faExclamationTriangle} className="me-2 fs-4" />
                                            <div>
                                                <strong>Atenção:</strong> Nota não encontrada ou divergente na base de dados central.
                                            </div>
                                        </div>
                                    );
                                }

                                return (
                                    <div className="alert alert-warning d-flex align-items-center" role="alert">
                                        <FontAwesomeIcon icon={faSync} spin className="me-2 fs-4" />
                                        <div>
                                            <strong>Aguardando:</strong> Validação OCR ainda não concluída.
                                        </div>
                                    </div>
                                );
                            })()}

                            <div className="mb-3 p-3 bg-light rounded border">
                                <div className="d-flex justify-content-between">
                                    <span><strong>Fatura:</strong> #{faturaEmAvaliacao.id}</span>
                                    <span><strong>Nota:</strong> {faturaEmAvaliacao.numeroNotaFiscal}</span>
                                </div>
                                <div className="d-grid mt-2">
                                    <button type="button" className="btn btn-outline-dark btn-sm" onClick={() => visualizarAnexo(faturaEmAvaliacao.idNotaFiscal!)}>
                                        <FontAwesomeIcon icon={faEye} className="me-2"/> Visualizar Anexo
                                    </button>
                                </div>
                            </div>
                            <div className="form-group">
                                <label className="text-danger fw-bold small">Motivo da Reprovação:</label>
                                <textarea className="form-control" rows={3} value={motivoReprovacao} onChange={e => setMotivoReprovacao(e.target.value)} placeholder="Obrigatório em caso de reprovação..."></textarea>
                            </div>
                        </div>
                        <div className="modal-footer justify-content-between">
                            <button className="btn btn-danger" onClick={() => processarAvaliacao(false)}><FontAwesomeIcon icon={faTimes}/> Reprovar</button>
                            <button className="btn btn-success" onClick={() => processarAvaliacao(true)}><FontAwesomeIcon icon={faCheck}/> Aprovar</button>
                        </div>
                    </div>
                </div>
            </div>
        )}

        {showModalDetalhes && faturaParaDetalhes && (
            <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
                <div className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">
                    <div className="modal-content">
                        <div className="modal-header bg-dark text-white">
                            <h5 className="modal-title">Itens da Fatura #{faturaParaDetalhes.id}</h5>
                            <button type="button" className="btn-close btn-close-white" onClick={() => setShowModalDetalhes(false)}></button>
                        </div>
                        <div className="modal-body p-0">
                            <table className="table table-sm table-striped mb-0">
                                <thead className="table-light">
                                    <tr>
                                        <th className="ps-3">Contrato</th>
                                        <th>Cliente</th>
                                        <th>Produto</th>
                                        <th className="text-end pe-3">Valor</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {faturaParaDetalhes.itens?.length > 0 ? faturaParaDetalhes.itens.map(item => (
                                        <tr key={item.id}>
                                            <td className="ps-3">{item.contrato?.numeroContrato || '-'}</td>
                                            <td>{item.contrato?.idCliente || '-'}</td>
                                            <td>{item.parametroUtilizado?.comissaoPerfil?.nome || '-'}</td>
                                            <td className="text-end pe-3">{formatMoney(item.valorComissao)}</td>
                                        </tr>
                                    )) : <tr><td colSpan={4} className="text-center py-3 text-muted">Sem itens.</td></tr>}
                                </tbody>
                            </table>
                        </div>
                        <div className="modal-footer">
                            <button className="btn btn-secondary" onClick={() => setShowModalDetalhes(false)}>Fechar</button>
                        </div>
                    </div>
                </div>
            </div>
        )}

      </div>
    </>
  );
}

export default GestaoNotaFiscalComponent;